import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */

// FOCUS-TRAP-001 (cycle-6 Path A): hand-rolled document keydown capture
//
// WebKit's <dialog>.showModal() does not enumerate <scroll-hint>'s focusable
// descendants (cycle-5 escalation diagnosis). Path C (focus-trap@8.x library)
// was empirically falsified on WebKit (rnd-sprint-2 cycle 1: bodyEscapeCount=5,
// uniqueElements=4 — identical to cycle-5 pattern).
//
// Root cause confirmed (focus-trap.js:801-811): checkKeyNav returns destinationNode=null
// for mid-ring Tab presses (not first/last boundary). Line 643 "else if (!isTabEvent(event))"
// means nextTabbableNode() is NOT called for Tab events in the mid-ring — the library
// intentionally falls through to "let the browser take care of [shift+]tab and move the focus"
// (focus-trap.js:811). On WebKit, that browser algorithm is broken for SUMMARY elements
// inside <dialog>.showModal(), causing the 3-element escape cycle (H1 confirmed).
//
// This Path A handler is registered on document in CAPTURE phase, fires
// synchronously BEFORE focus moves on Tab keypress, and uses our own focusable
// enumeration (not WebKit's broken native ring) to compute the correct
// next/previous focusable. event.preventDefault() stops WebKit's broken
// algorithm from executing; element.focus() programmatically moves focus to
// the correct destination.
//
// This is the engine-agnostic guarantee — works identically on WebKit + Chromium.

/** @type {string} */
const FOCUSABLE_SELECTOR =
  'a[href]:not([disabled]):not([aria-hidden="true"]):not([inert]),' +
  'button:not([disabled]):not([aria-hidden="true"]):not([inert]),' +
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden="true"]):not([inert]),' +
  'select:not([disabled]):not([aria-hidden="true"]):not([inert]),' +
  'textarea:not([disabled]):not([aria-hidden="true"]):not([inert]),' +
  'summary:not([aria-hidden="true"]):not([inert]),' +
  '[tabindex]:not([tabindex="-1"]):not([aria-hidden="true"]):not([inert])';

class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * S1 FIX ROUND 4: observes the header section for external writes to the
   * bag button's aria-expanded attribute (see #installBagButtonObserver).
   * @type {MutationObserver | null}
   */
  #bagButtonObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // S1 FIX ROUND 2 (2026-08-05, WCAG 4.1.2 auto-open race): sync the header
    // bag button's aria-expanded state from THIS component's own lifecycle
    // instead of a load-order-dependent listener attached later from
    // sections/header.liquid. The header.liquid {% javascript %} IIFE queried
    // this element and attached 'dialog:open'/'dialog:close' listeners at
    // script-execution time, which raced (and lost to) the first real
    // dialog:open fired by an ATC at realistic page dwell (~2-3s) — the
    // listener simply didn't exist yet when that first event fired.
    // Attaching here in connectedCallback (custom-element upgrade time, which
    // always precedes any user-triggered open) makes the race structurally
    // impossible: this listener exists before any dialog:open can fire.
    // The bag button is resolved LAZILY inside the handler (not cached here)
    // because it lives in sections/header.liquid, a DOM sibling rendered
    // before this component — querying at event time avoids any ordering
    // assumption about header vs. header-actions render position.
    this.addEventListener(DialogOpenEvent.eventName, this.#handleAriaExpandedOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleAriaExpandedClose);

    // S1 FIX ROUND 4 (2026-08-05, QA pass 3 — round-3 updatedCallback()
    // proven structurally unreachable; see the removed override this
    // replaces, and #installBagButtonObserver below for the full account).
    this.#installBagButtonObserver();

    // S1 FIX ROUND 5 (2026-08-05, QA pass 4 item D3): the CartUpdateEvent-
    // driven header refresh that used to be wired here (defects 2/3, count +
    // aria-label staleness after quantity mutations) was RELOCATED to
    // assets/header-bag-refresh.js — this component never instantiates on
    // /cart (snippets/header-actions.liquid:102), the one page the refresh
    // targeted, so the listener was structurally inert there. See that
    // module's header comment for the full account, including why it was
    // removed from here rather than left as a harmless duplicate: on
    // non-cart drawer pages both this component AND the new module are live
    // simultaneously, so keeping both would double-fire
    // sectionRenderer.renderSection() on every cart mutation.

    // FOCUS-TRAP-001 (cycle-6 Path A): register document-level Tab capture handler.
    // capture=true fires BEFORE focus moves, allowing us to call event.preventDefault()
    // before WebKit's broken native showModal() algorithm executes.
    document.addEventListener('keydown', this.#handleTabCapture, true);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleAriaExpandedOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleAriaExpandedClose);

    this.#bagButtonObserver?.disconnect();
    this.#bagButtonObserver = null;

    // FOCUS-TRAP-001 (cycle-6 Path A): remove document-level Tab capture handler.
    document.removeEventListener('keydown', this.#handleTabCapture, true);

    this.#historyAbortController?.abort();
  }

  /**
   * S1 FIX ROUND 2: resolves the header bag button lazily (at event time,
   * not cached at connectedCallback) and syncs its aria-expanded state to
   * match this dialog's actual open/closed state. Covers every open path
   * (bag-icon click, ATC auto-open, and the hidden Horizon internal
   * trigger) and every close path (Escape, close button, outside click,
   * popstate) uniformly, because all of them route through showDialog() /
   * closeDialog() on the base DialogComponent, which is what dispatches
   * DialogOpenEvent / DialogCloseEvent.
   *
   * S1 FIX ROUND 4: this is now ALSO the single write path used by
   * #installBagButtonObserver's correction callback (see below), so it
   * compares-before-writing. This is load-bearing, not cosmetic: a
   * MutationObserver watching this same attribute would otherwise see its
   * own corrective setAttribute() call as a new mutation (setAttribute
   * queues a mutation record on every call, even when the value is
   * unchanged — confirmed against the WHATWG DOM "change an attribute"
   * algorithm, which has no same-value early return) and re-invoke this
   * method forever. Comparing first means the loop always terminates after
   * exactly one corrective write: the correction no longer disagrees with
   * `expected`, so the next observer callback for that write is a no-op.
   *
   * @param {boolean} expanded
   */
  #syncBagButtonAriaExpanded(expanded) {
    const bagBtn = document.querySelector('[data-cart-open]');
    if (!bagBtn) return;
    const expectedValue = expanded ? 'true' : 'false';
    if (bagBtn.getAttribute('aria-expanded') !== expectedValue) {
      bagBtn.setAttribute('aria-expanded', expectedValue);
    }
  }

  #handleAriaExpandedOpen = () => this.#syncBagButtonAriaExpanded(true);
  #handleAriaExpandedClose = () => this.#syncBagButtonAriaExpanded(false);

  /**
   * S1 FIX ROUND 4 (2026-08-05, QA pass 3 — round-3 `updatedCallback()`
   * PROVEN structurally unreachable, not merely raced):
   *
   * Round 3 relied on `updatedCallback()` (assets/component.js:90, base
   * `Component` class) firing via morph.js's `onAfterUpdate` hook
   * (assets/morph.js:82-86, queued with queueMicrotask for every walked
   * node that `instanceof Component`) after every 'header' section morph.
   * QA pass 3 instrumented this directly (monkey-patched
   * `CartDrawerComponent.prototype.updatedCallback`, diag-refs-result.json):
   * ZERO fires across every mutation path tested. Root cause, isolated in
   * a standalone probe (diag-domparser-upgrade.mjs): morph.js:234 calls
   * `options.onAfterUpdate?.(newNode)` — but `newNode` in the section-morph
   * path (assets/section-renderer.js morphSection()) is sourced from
   * `new DOMParser().parseFromString(html, 'text/html')`, and elements
   * parsed via DOMParser are NEVER custom-element-upgraded by the browser
   * (no browsing context on the parsed document — confirmed empirically:
   * a `diag-foo` custom element defined via `customElements.define` BEFORE
   * parsing is still a bare `HTMLElement`, not `instanceof` its own class,
   * when read back from a DOMParser tree). So morph.js:83's
   * `node instanceof Component` guard fails on EVERY walked node on EVERY
   * 'header' morph, unconditionally — the round-3 hook was dead code from
   * the moment it was written, not a race that got worse under load.
   *
   * This can be corrected two ways: (a) fix morph.js:234 to pass the live,
   * already-upgraded `oldNode` instead of `newNode` — a genuine upstream
   * bug given morph.js:83's own intent — or (b) stop depending on the
   * morph-invoked component lifecycle for this defect entirely. (a) was
   * evaluated and REJECTED for this round: `onAfterUpdate` is the DEFAULT
   * hook on `MORPH_OPTIONS`, consumed (via spread or implicitly, as the
   * `morph()` default parameter) by every unguarded `morph()` call site in
   * the theme — product-card.js, product-form.js (x3), variant-picker.js
   * (x3), quick-add.js, predictive-search.js (x2), local-pickup.js, plus
   * section-renderer.js's morphSection() used fleet-wide. Because
   * `onAfterUpdate` has NEVER fired correctly at any of those sites either
   * (same DOMParser-sourced-newNode defect, structurally, everywhere),
   * fixing it starts firing `updatedCallback()` on every Component at every
   * one of those sites for the first time ever, in this permanent-flow
   * initiative, with no budget this round to audit each site's Component
   * subclasses for behavior that was silently inert until now. That is an
   * unbounded, cross-cutting blast radius for a fix whose OWN correctness
   * would still be gated on morph timing/ordering internals — the same
   * fragility class QA pass 3 just falsified. (b) is what ships: see
   * #installBagButtonObserver, a MutationObserver watching the DOM
   * attribute directly — the same idiom the base `Component` class itself
   * already uses (assets/component.js #mutationObserver, watching `ref`
   * attributes) to defend against exactly this class of post-morph
   * staleness, rather than trusting morph.js's callback plumbing.
   */
  #installBagButtonObserver() {
    // `.closest('.shopify-section')` — NOT a hardcoded id. Direct-inspection
    // correction (2026-08-05): the header section's REAL runtime id is a
    // theme-instance-numeric-id + JSON-key compound (confirmed against live
    // capture, s1qa2/diag-network.json and s1qa3/assertion-a-results.json:
    // `section_id=sections--22242979938528__header_section`), not the
    // literal string "header" narratively used in round-3's comments.
    // `<cart-drawer-component>` is rendered from WITHIN that section
    // (sections/header.liquid -> snippets/header-actions.liquid), so its
    // own nearest `.shopify-section` ancestor IS that wrapper regardless of
    // the exact id string — no id-string assumption required at all here.
    const headerSection = this.closest('.shopify-section') ?? document.body;
    this.#bagButtonObserver = new MutationObserver((records) => {
      for (const record of records) {
        if (!(record.target instanceof HTMLElement) || !record.target.matches('[data-cart-open]')) continue;
        this.#syncBagButtonAriaExpanded(this.refs.dialog?.open ?? false);
      }
    });
    this.#bagButtonObserver.observe(headerSection, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
      subtree: true,
    });
  }

  // S1 FIX ROUND 5 (2026-08-05, QA pass 4 item D3): #getHeaderSectionId and
  // #handleHeaderRefresh (the CartUpdateEvent-driven header hydration
  // refresh, formerly here) were REMOVED and relocated to
  // assets/header-bag-refresh.js, a small always-present module loaded from
  // sections/header.liquid regardless of `cart_type`/`template.name`. This
  // component only ever instantiates when a drawer exists
  // (settings.cart_type == 'drawer' and template.name != 'cart' —
  // snippets/header-actions.liquid:102), so a header-refresh mechanism
  // homed here was structurally inert on /cart, the one page it targeted
  // (QA pass 4 netlog: zero header fetches on /cart mutations). D2 (the
  // in-drawer refresh path via data-hydration-key="bag-button" +
  // cart-items-component's own hydration morph of its own section id) is
  // UNAFFECTED by this removal — that path never depended on this listener.

  // FOCUS-TRAP-001 (cycle-6 Path A): document-level Tab capture handler.
  //
  // Intercepts EVERY Tab/Shift+Tab keypress when the cart drawer dialog is open.
  // Computes the full focusable ring via querySelectorAll (not WebKit's broken native
  // enumeration) and calls event.preventDefault() + element.focus() for EVERY Tab press.
  //
  // CRITICAL: We must intercept ALL Tab presses, not just boundary wraps.
  // WebKit's broken showModal() native algorithm skips elements mid-ring (e.g.,
  // SUMMARY → BODY instead of SUMMARY → Check out, and also skips A links entirely).
  // Path C (focus-trap@8.x) only intercepted at boundaries and let the browser handle
  // mid-ring — same 5-escape failure pattern. Calling event.preventDefault() on every
  // Tab prevents WebKit's broken algorithm from running at any step.
  //
  // WebKit body-escape safety: if focus is somehow outside the dialog when Tab fires,
  // redirect to the first focusable element inside the dialog.
  #handleTabCapture = (event) => {
    if (event.key !== 'Tab') return;

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) return;

    // SUMMARY HANDLING: Tab on a <summary> element triggers the browser's native
    // details-toggle behavior BEFORE capture-phase keydown fires. By the time our
    // handler runs, details.open is already true, so a rect-only filter wrongly
    // includes the now-visible inner inputs and routes focus to INPUT (which then
    // silently falls back to SUMMARY because the input is not yet interactable in
    // the same tick). We prevent the toggle race by closing the details explicitly
    // if Tab is pressed while a SUMMARY is focused. The user uses Enter/Space to
    // intentionally expand the section; Tab is for ring traversal only.
    const activeSummary = document.activeElement?.tagName === 'SUMMARY' ? document.activeElement : null;
    if (activeSummary) {
      const details = activeSummary.closest('details');
      if (details?.open) {
        details.open = false;
      }
    }

    const focusables = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => {
        // Skip elements with zero box (visibility: hidden, display: none)
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        // Always exclude children of <details> from the Tab ring regardless of
        // open/closed state. <summary> itself stays in the ring; its children
        // (discount inputs, apply button) are accessible via Enter/Space on
        // SUMMARY, not Tab. This gives a stable 9-element ring at all times.
        const enclosingDetails = el.closest('details');
        if (enclosingDetails && el.tagName !== 'SUMMARY') {
          return false;
        }

        return true;
      });

    if (focusables.length === 0) return;

    const first = focusables[0];
    const active = document.activeElement;

    // Always preventDefault — never let WebKit's broken native algorithm run
    event.preventDefault();

    // WebKit body-escape safety: if focus is outside dialog (escaped), redirect to first
    if (!dialog.contains(active)) {
      first.focus();
      return;
    }

    const currentIdx = focusables.indexOf(active);

    /** @type {Element} */
    let target;

    if (event.shiftKey) {
      // Shift+Tab: go to previous, wrap from first to last
      const prevIdx = currentIdx <= 0 ? focusables.length - 1 : currentIdx - 1;
      target = focusables[prevIdx];
    } else {
      // Tab: go to next, wrap from last to first
      const nextIdx = currentIdx >= focusables.length - 1 ? 0 : currentIdx + 1;
      target = focusables[nextIdx];
    }

    target.focus();

    // WebKit SUMMARY workaround: Tab on a focused <summary> element toggles
    // its parent <details> open/closed and may re-route focus back to the
    // <summary> even after event.preventDefault() + our .focus() call above.
    // Scheduling a queueMicrotask re-focus ensures our target wins the last
    // focus assignment after WebKit's internal details-toggle focus callback.
    if (active.tagName === 'SUMMARY') {
      queueMicrotask(() => {
        if (document.activeElement !== target) {
          target.focus();
        }
      });
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * @param {Event} [event] - The originating click event. Used to capture the
   *   trigger element BEFORE the dispatch chain blurs it (WCAG 2.4.3 / FOCUS-RETURN-001).
   */
  open(event) {
    // FOCUS-RETURN-001: capture the trigger at click-event origin — before the
    // on:click dispatch chain can blur the button — then thread it into showDialog
    // so focus returns to it on close (ESC + close-button paths).
    const trigger = event?.target instanceof HTMLElement ? event.target : null;
    this.showDialog(trigger);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
